import React from 'react';

import {
  AutocompleteProps,
  ListItem,
  ListItemText,
  Typography,
  Autocomplete,
} from '@mui/material';
import { VariableSizeList, ListChildComponentProps } from 'react-window';

import { colors } from 'config/theme';

const useResetCache = (data: any) => {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
};

const defaultRender = (props: ListChildComponentProps) => {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + 8,
  };
  const organization = dataSet[1];
  const option = dataSet[0];
  return (
    <ListItem
      {...option}
      style={inlineStyle}
      sx={{
        ':hover': {
          backgroundColor: colors.lightGray,
        },
      }}
    >
      <ListItemText
        primary={
          <>
            <Typography display='inline' variant='body2'>
              {organization.name}
            </Typography>
            <Typography
              display='inline'
              variant='body2'
              sx={{ color: colors.uswds.base.light }}
            >{` (${organization.candidate_count})`}</Typography>
            <Typography
              variant='body2'
              display='inline'
              color={organization.parent_id ? 'primary.main' : 'success.main'}
            >
              {organization.parent_id ? '  AE' : '  ORG'}
            </Typography>
          </>
        }
      />
    </ListItem>
  );
};

interface OrganizationAutocompleteProps
  extends AutocompleteProps<any, boolean, true, true> {
  renderrow?: (props: ListChildComponentProps) => JSX.Element;
  characterBreak?: number;
}
export const OrganizationAutocomplete = (
  props: OrganizationAutocompleteProps,
) => {
  const { renderrow, characterBreak, ...rest } = props;
  return (
    <Autocomplete
      {...rest}
      disableListWrap
      ListboxComponent={React.forwardRef<
        HTMLDivElement,
        React.HTMLAttributes<HTMLElement>
      >(function ListboxComponent(props, ref) {
        const { children, ...other } = props;
        const itemData: Array<React.ReactChild> = [];
        (children as Array<React.ReactChild>).forEach(
          (item: React.ReactChild & { children?: Array<React.ReactChild> }) => {
            itemData.push(item);
            itemData.push(...(item.children || []));
          },
        );

        const itemCount = itemData.length;
        const itemSize = 40;
        const OuterElementContext = React.createContext({});
        const OuterElementType = React.forwardRef<HTMLDivElement>(
          (props, ref) => {
            const outerProps = React.useContext(OuterElementContext);
            return <div ref={ref} {...props} {...outerProps} />;
          },
        );

        const getChildSize = (child: any) => {
          const employer = child[1];
          if (employer.name.length > (characterBreak ? characterBreak : 25)) {
            return 60;
          }

          return itemSize;
        };

        const getHeight = () => {
          if (itemCount > 8) {
            return 8 * itemSize;
          }
          return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
        };

        const gridRef = useResetCache(itemCount);

        return (
          <div ref={ref}>
            <OuterElementContext.Provider value={other}>
              <VariableSizeList
                itemData={itemData}
                height={getHeight() + 2 * 8}
                width='100%'
                ref={gridRef}
                outerElementType={OuterElementType}
                innerElementType='ul'
                itemSize={(index: any) => getChildSize(itemData[index])}
                overscanCount={5}
                itemCount={itemCount}
              >
                {renderrow || defaultRender}
              </VariableSizeList>
            </OuterElementContext.Provider>
          </div>
        );
      })}
    />
  );
};
